import { useTooltipData } from '../../../core/components/overlay-tooltip/hooks/useTooltipData.js';
import { OverlayTooltip } from '../../../core/components/overlay-tooltip/OverlayTooltip.js';
import type { Data } from '../../../core/components/overlay-tooltip/types.js';
import { isTooltipDataEmpty } from '../../../core/components/overlay-tooltip/utils.js';
import type { SeriesActionsTemplate } from '../../../core/types/series-actions-template.js';
import { useTreeMapCustomTooltip } from '../../hooks/useTreeMapCustomTooltip.js';
import { useTruncationMode } from '../../hooks/useTruncationMode.js';
import { TreeMapTooltipPayload } from '../../types/treemap-chart.config.js';

type SeriesData = Data['series'][number];

type TreeMapMetadata = {
  id: string;
  value: number;
  path?: string[];
  children?: string[];
};

interface TreeMapOverlayTooltipProps {
  actionsForItem?: (
    item: SeriesData,
  ) => (() => SeriesActionsTemplate) | undefined;
}

export const TreeMapOverlayTooltip = (props: TreeMapOverlayTooltipProps) => {
  const data = useTooltipData().data;
  const truncationMode = useTruncationMode();
  const customTooltip = useTreeMapCustomTooltip();

  if (customTooltip?.hidden || isTooltipDataEmpty(data)) {
    return null;
  }

  const series = data.series[0];
  const meta = data.metadata as TreeMapMetadata;
  if (customTooltip?.template) {
    const payload: TreeMapTooltipPayload = {
      hierarchyPath: meta.path ?? [series.name as string],
      value: meta.value,
      childrenCount: meta.children,
    };
    return <OverlayTooltip template={customTooltip.template(payload)} />;
  }

  return (
    <OverlayTooltip truncation={truncationMode}>
      <OverlayTooltip.Header header1={data.header1} />
      <OverlayTooltip.SeriesItem
        name={series.name}
        value={series.value}
        color={series.color}
        actions={props.actionsForItem?.(series)}
      />
    </OverlayTooltip>
  );
};

TreeMapOverlayTooltip['displayName'] = 'TreeMapOverlayTooltip';
